import { t } from "i18next";
import { z } from "zod";

import {
  enrolmentsDocumentsTypesSchema,
  insertEnrolmentsNotesTypeSchema,
  publicEnrolmentStatusEnumSchema,
  publicInvoiceStatusEnumSchema,
  publicProvinceEnumSchema,
  publicRegistrationStatusEnumSchema,
  registrationFormSchema,
  rowDocumentTypesTypeSchema,
  rowEnrolmentGeneralDocumentsTypeSchema,
  rowEnrolmentsDocumentsTypesTypeSchema,
  rowEnrolmentsNotesTypeSchema,
  rowEnrolmentsProductsTypeSchema,
  rowEnrolmentsTypeSchema,
  rowProductsTypeSchema,
  rowStaffTypeSchema,
  updateEnrolmentsTypeSchema,
} from "@libs/schemas";
import { formatEnrolmentStatus } from "@/utils/enrolments";

import { campusSchema } from "./campus";
import { certificateSchema } from "./certificate";
import { courseSchema } from "./course";
import { enrolmentAchievementSchema } from "./enrolmentsAchievements";
import { ProductType } from "./product";
import { studentSchema } from "./student";

export const enrolmentSchema = rowEnrolmentsTypeSchema.merge(
  z.object({
    student: studentSchema
      .pick({
        id: true,
        first_name: true,
        last_name: true,
        middle_name: true,
        email: true,
        phone_number: true,
        date_of_birth: true,
        sin_number: true,
        encrypted_sin: true,
        student_number: true,
      })
      .partial({
        email: true,
        phone_number: true,
        date_of_birth: true,
        sin_number: true,
        encrypted_sin: true,
        student_number: true,
      }),
    campus: campusSchema
      .pick({
        id: true,
        name: true,
        short_code: true,
        province: true,
      })
      .partial({
        short_code: true,
      }),
    course: courseSchema
      .pick({
        id: true,
        name: true,
        length_in_days: true,
        observation_sessions_count: true,
        lessons: true,
        achievements: true,
      })
      .partial({
        observation_sessions_count: true,
        lessons: true,
      }),
    certificate: certificateSchema.nullish(),
    enrolment_achievements: enrolmentAchievementSchema,
    products: z.array(
      rowProductsTypeSchema.pick({ id: true, name: true }).merge(
        rowEnrolmentsProductsTypeSchema
          .pick({
            product_name: true,
            price: true,
            has_fixed_price: true,
          })
          .merge(
            z.object({
              enrolment_product_id: z.string().uuid(),
            }),
          ),
      ),
    ),
    enrolments_documents_types: z.array(
      rowEnrolmentsDocumentsTypesTypeSchema
        .pick({
          id: true,
          uploaded_at: true,
          approved_at: true,
          rejected_at: true,
          rejection_reason: true,
          status: true,
          storage_keys: true,
        })
        .extend({
          document_type: rowDocumentTypesTypeSchema.pick({
            id: true,
            name: true,
          }),
        }),
    ),
    enrolments_notes: z.array(
      rowEnrolmentsNotesTypeSchema
        .pick({
          id: true,
          created_at: true,
          updated_at: true,
          content: true,
          staff_id: true,
        })
        .extend({
          staff: rowStaffTypeSchema
            .pick({
              id: true,
              first_name: true,
              last_name: true,
            })
            .nullable(),
        }),
    ),
  }),
);

export type EnrolmentType = z.infer<typeof enrolmentSchema>;

export const enrolmentInvoiceSummarySchema = z.object({
  outstanding: z.number(),
  invoice_status: publicInvoiceStatusEnumSchema.nullable(),
  invoice_count: z.number(),
  payment_status: z
    .enum(["OVERDUE", "PAID", "ON_TRACK", "OUTSTANDING"])
    .nullable(),
  placeholder_status: publicInvoiceStatusEnumSchema.nullable(),
});

export type EnrolmentInvoiceSummaryType = z.infer<
  typeof enrolmentInvoiceSummarySchema
>;

export const enrolmentListSchema = enrolmentSchema
  .omit({
    enrolment_achievements: true,
    products: true,
    enrolments_documents_types: true,
    enrolments_notes: true,
  })
  .extend({
    course: courseSchema.pick({
      id: true,
      name: true,
      length_in_days: true,
    }),
    student: studentSchema
      .pick({
        id: true,
        first_name: true,
        last_name: true,
        student_number: true,
        date_of_birth: true,
      })
      .partial({ date_of_birth: true }),
  })
  .merge(enrolmentInvoiceSummarySchema);

export type EnrolmentListSchemaType = z.infer<typeof enrolmentListSchema>;

export type EnrolmentsProductType = z.infer<
  typeof rowEnrolmentsProductsTypeSchema
> & {
  product?: ProductType | null;
};

export const enrolmentFilterSchema = z.object({
  search: z.string().optional().catch(""),
  statuses: z.array(publicEnrolmentStatusEnumSchema).optional().catch([]),
  registrationStatuses: z
    .array(publicRegistrationStatusEnumSchema)
    .optional()
    .catch([]),
  courseIds: z.array(z.string()).optional().catch([]),
  campusesIds: z.array(z.string()).optional().catch([]),
});
export type EnrolmentFilterType = z.infer<typeof enrolmentFilterSchema>;

export const enrolmentSearchParamsSchema = enrolmentFilterSchema.extend({
  id: z.string().uuid().optional().catch(""),
});

// Status options for dropdown menus
export const enrolmentStatusOptions =
  publicEnrolmentStatusEnumSchema.options.map(status => ({
    label: formatEnrolmentStatus(status),
    value: status,
  }));

export const updateRegistrationFormSchema = updateEnrolmentsTypeSchema
  .merge(registrationFormSchema)
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
  });
export type UpdateRegistrationFormType = z.infer<
  typeof updateRegistrationFormSchema
>;

export type EnrolmentStatusType = z.infer<
  typeof publicEnrolmentStatusEnumSchema
>;

export const addProductsProductTypeSchema = z.enum(["EXISTING", "CUSTOM"]);

export const addProductsToEnrolmentSchema = z.object({
  product_type: addProductsProductTypeSchema,
  product_id: z.string().uuid().optional(),
  product_name: z.string().optional(),
});

export type AddProductsToEnrolmentType = z.infer<
  typeof addProductsToEnrolmentSchema
>;

export const enrolmentFormSchema = z.object({
  enrolment_achievements: enrolmentAchievementSchema,
  enrolments_documents_types: z
    .array(enrolmentsDocumentsTypesSchema)
    .default([]),
});

export type EnrolmentFormType = z.infer<typeof enrolmentFormSchema>;

export type EnrolmentGeneralDocumentsType = z.infer<
  typeof rowEnrolmentGeneralDocumentsTypeSchema
>;

export const enrolmentSelectionFormSchema = z.object({
  enrolment_id: z.string().uuid(),
});

export type EnrolmentSelectionFormSchemaType = z.infer<
  typeof enrolmentSelectionFormSchema
>;

export const studentContractSchema = z.object({
  date_of_birth: z.string().nullable(),
  street_address: z.string().nullable(),
  middle_name: z
    .string()
    .nullish()
    .transform(val => (val?.trim() === "" ? null : val)),
  city: z.string().nullable(),
  province: publicProvinceEnumSchema.nullish().transform(val => val ?? null),
  postal_code: z.string().nullable(),
  driver_licence_number: z
    .string()
    .nullish()
    .transform(val => (val?.trim() === "" ? null : val)),
  driver_licence_class: z
    .string()
    .nullable()
    .transform(val => (val?.trim() === "" ? null : val)),
  driver_licence_expiry: z.string().nullable(),
  sin_number: z
    .string()
    .nullish()
    .transform(val => (val?.trim() === "" ? null : val))
    .refine(val => val == null || /^\d+$/.test(val), {
      message: t("main:registrations.details.contract.sinError"),
    }),
  next_of_kin_name: z.string().optional().nullable(),
  next_of_kin_phone_number: z
    .string()
    .nullish()
    .transform(val => (val?.trim() === "" ? null : val)),
});

export type StudentContractFormType = z.infer<typeof studentContractSchema>;

//MELT upload form
export const meltFormSchema = z.object({
  melt_uploaded_at: z.string().nullable(),
});

export type MeltFormType = z.infer<typeof meltFormSchema>;

export const updateAdvancedStandingFormSchema = updateEnrolmentsTypeSchema
  .pick({
    has_advanced_standing: true,
    advanced_standing_details: true,
  })
  .superRefine((data, ctx) => {
    if (
      data.has_advanced_standing &&
      (!data.advanced_standing_details ||
        data.advanced_standing_details.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("common:validation.requiredField"),
        path: ["advanced_standing_details"],
      });
    }
  });

export type UpdateAdvancedStandingFormType = z.infer<
  typeof updateAdvancedStandingFormSchema
>;

export const createEnrolmentsNotesFormSchema = insertEnrolmentsNotesTypeSchema;

export type CreateEnrolmentsNotesFormType = z.infer<
  typeof createEnrolmentsNotesFormSchema
>;

export const registerEnrolmentNotesFormSchema =
  insertEnrolmentsNotesTypeSchema.pick({
    content: true,
    staff_id: true,
  });

export type RegisterEnrolmentNotesFormType = z.infer<
  typeof registerEnrolmentNotesFormSchema
>;

export const updateNoteContentFormSchema = z.object({
  content: z.string(),
});

export type UpdateNoteContentFormType = z.infer<
  typeof updateNoteContentFormSchema
>;

export const updateEnrolmentCourseFormSchema = updateEnrolmentsTypeSchema.pick({
  campus_id: true,
  course_id: true,
  start_date: true,
  end_date: true,
});

export type UpdateEnrolmentCourseFormType = z.infer<
  typeof updateEnrolmentCourseFormSchema
>;

export const updateEnrolmentPriceFormSchema = registrationFormSchema.pick({
  enrolments_products: true,
});

export type UpdateEnrolmentPriceFormType = z.infer<
  typeof updateEnrolmentPriceFormSchema
>;

export const periodReportFormSchema = z.object({
  year: z.coerce.number().int().positive(),
  period: z.string().min(1, "Period is required").nullish(),
});

export type PeriodReportFormType = z.infer<typeof periodReportFormSchema>;

export type EnrolmentReportDataType = Pick<
  EnrolmentType,
  | "id"
  | "student"
  | "campus"
  | "course"
  | "start_date"
  | "end_date"
  | "street_address"
  | "city"
  | "province"
  | "postal_code"
  | "diploma_date"
>;

export type UpdateEnrolmentType = z.infer<typeof updateEnrolmentsTypeSchema>;
